import { fetchShipmentDetail } from 'api/shipping';
import Breadcrumbs from 'components/framework/Breadcrumbs';
import { Column, useSortBy, useTable } from 'react-table';
import Layout from 'components/ui/Layout';
import SectionHeader from 'components/ui/SectionHeader';
import React, { useState, FC, useContext } from 'react';
import { ShipmentPageModel } from 'types/page-types';
import { ShipmentDetail, ShipmentItem } from 'types/shipment';
import { LocalizationContext } from 'context/localization.context';
import { handleSortByKeyDown } from 'components/ui/Table';
import Icon from 'components/ui/Icon';
import clsx from 'clsx';
const ShipmentPage: FC<ShipmentPageModel> = ({
	shipments,
	detailModel,
	heading,
	id,
	orderDate,
	orderId,
	breadcrumbs,
}) => {
	const [shipmentDetail, setShipmentDetail] = useState<ShipmentDetail | null>(
		detailModel
	);
	const [shipmentsList, setShipmentsList] = useState<ShipmentItem[]>(shipments);
	const [isValidating, setIsValidating] = useState<boolean>(false);

	const { t } = useContext<any>(LocalizationContext);

	const handleSeeAllDetails = async (row: ShipmentItem) => {
		// update selected flags
		const updatedData = shipmentsList.map((item) => ({
			...item,
			selected: item.id === row.id ? true : false,
		}));
		setShipmentsList(updatedData);
		setIsValidating(true);
		// Ensure spinner is visible for at least 2s to avoid flicker
		const start = Date.now();
		try {
			const response: ShipmentDetail = await fetchShipmentDetail(row.link);
			setShipmentDetail(response);
		} catch (error) {
			console.error('Failed to fetch:', error);
		} finally {
			const elapsed = Date.now() - start;
			const minDuration = 1000;

			if (elapsed < minDuration) {
				setTimeout(() => setIsValidating(false), minDuration - elapsed);
			} else {
				setIsValidating(false);
			}
		}
	};

	const columns: Column<ShipmentItem>[] = React.useMemo(
		() => [
			{
				Header: t('shipmentpage/tracking/trackingnumber'),
				accessor: 'id',
			},
			{
				Header: t('shipmentpage/tracking/status'),
				accessor: 'status',
			},
			{
				Header: t('shipmentpage/tracking/eta'),
				accessor: 'arrivalTime',
			},
			{
				Header: t('shipmentpage/tracking/modeofdelivery'),
				accessor: 'deliveryMode',
			},
			{
				Header: t('shipmentpage/tracking/goodsreceiver'),
				accessor: 'goodsReceiver',
			},
			{
				Header: '',
				accessor: 'link',
				Cell: ({ row }) => {
					const item = row.original;
					return (
						<button
							className={clsx(
								'flex gap-4 items-center  hover:underline',
								item.selected && 'text-blue font-semibold underline'
							)}
							onClick={() => handleSeeAllDetails(item)}
						>
							{t('shipmentpage/tracking/seealllink')}
							<span
								className={clsx(item.selected ? 'block fill-blue' : 'hidden')}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
									className="feather feather-check-circle"
								>
									<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
									<polyline points="22 4 12 14.01 9 11.01"></polyline>
								</svg>
							</span>
						</button>
					);
				},
			},
		],
		[shipmentsList]
	);
	const data = React.useMemo(() => shipmentsList, [shipmentsList]);
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
	} = useTable(
		{
			columns,
			data,
			initialState: {
				sortBy: [
					{
						id: 'id',
						desc: true,
					},
				],
			},
		},
		useSortBy
	);
	return (
		<main id="main-content">
			{breadcrumbs && (
				<Layout>
					<Breadcrumbs links={breadcrumbs} />
				</Layout>
			)}
			<Layout>
				<SectionHeader heading={heading} size={'large'} />
				<p className="mb-4">
					<span className="font-semibold">
						{t('shipmentpage/tracking/orderid')}
					</span>
					<span className="ml-2 uppercase">{orderId}</span>
				</p>
				<p className="mb-4">
					{t('shipmentpage/tracking/orderdate')}
					<span className="ml-2">{orderDate}</span>
				</p>
				<h4 className="mt-16 mb-4 font-semibold">
					{t('shipmentpage/tracking/shipmentsheading')}
				</h4>
				<table
					{...getTableProps({ role: 'grid' })}
					className="border-spacing-0 w-full"
				>
					<caption className="sr-only">{heading}</caption>
					<thead className="bg-blue">
						{headerGroups.map((headerGroup) => (
							<tr
								className="text-white text-2xl p-1"
								{...headerGroup.getHeaderGroupProps()}
							>
								{headerGroup.headers.map((column, i) => (
									<th
										className="px-4 py-2 text-2xl font-normal text-left whitespace-no-wrap"
										{...column.getHeaderProps(
											column.getSortByToggleProps({
												title: `${t('shared/table/sortby')} ${column.Header}`,
												role: 'button',
												tabIndex: 0,
											})
										)}
										aria-sort={
											column.isSorted && !column.isSortedDesc
												? t('shared/table/ascending')
												: t('shared/table/descending')
										}
										data-header-col={i}
										onKeyDown={(e) => {
											if (column.canSort) {
												return handleSortByKeyDown(
													e,
													column,
													i,
													columns.length
												);
											}
										}}
									>
										{column.render('Header')}
										<div className="relative w-2 inline-block ml-1">
											{column.isSorted && column.isSortedDesc && (
												<Icon icon="sortDescending" />
											)}
											{column.isSorted && !column.isSortedDesc && (
												<Icon icon="sortAscending" />
											)}
										</div>
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody {...getTableBodyProps()}>
						{rows.map((row) => {
							prepareRow(row);
							return (
								<tr
									className={clsx(
										' text-2xl',
										row.original.selected
											? 'bg-blueLight'
											: 'odd:bg-white even:bg-greyLight'
									)}
									{...row.getRowProps()}
								>
									{row.cells.map((cell) => (
										<td
											className="px-4 py-2 whitespace-no-wrap"
											{...cell.getCellProps()}
										>
											{cell.render('Cell')}
										</td>
									))}
								</tr>
							);
						})}
					</tbody>
				</table>

				{shipmentDetail && (
					<div className="mt-16">
						<h3 className="mt-0 mb-8 text-blue text-h2   pb-4 border-b-1 border-greyDark">
							{t('shipmentpage/detail/shipmentdetailheading')}
						</h3>
						{isValidating ? (
							<div className="flex justify-center items-center my-20">
								<Icon icon="loader" size={3} animate="spin" />
							</div>
						) : (
							<div className="w-full md:w-2/3 mb-20">
								<dl className="grid grid-cols-2 gap-x-6 gap-y-3 p-4 text-p bg-white rounded-xl shadow">
									{shipmentDetail.trackingNumber && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/trackingnumber')}
											</dt>
											<dd>{shipmentDetail.trackingNumber}</dd>
										</>
									)}
									{shipmentDetail.deliveryMode && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/modeofdelivery')}
											</dt>
											<dd>{shipmentDetail.deliveryMode}</dd>
										</>
									)}
									{shipmentDetail.orderId && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/orderid')}
											</dt>
											<dd>{shipmentDetail.orderId}</dd>
										</>
									)}
									{shipmentDetail.includedOrders && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/shipmentorders')}
											</dt>
											<dd>{shipmentDetail.includedOrders}</dd>
										</>
									)}
									{shipmentDetail.customerNumber && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/customernumber')}
											</dt>
											<dd>{shipmentDetail.customerNumber}</dd>
										</>
									)}
									{shipmentDetail.requestedDeliveryDate && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/requesteddeliverydate')}
											</dt>
											<dd>{shipmentDetail.requestedDeliveryDate}</dd>
										</>
									)}
									{shipmentDetail.estimatedArrivalTime && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/eta')}
											</dt>
											<dd>{shipmentDetail.estimatedArrivalTime}</dd>
										</>
									)}
									{shipmentDetail.deliveryAddress && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/deliveryaddress')}
											</dt>
											<dd className="flex flex-col gap-2">
												<span>{shipmentDetail.deliveryAddress.name}</span>
												<span>{shipmentDetail.deliveryAddress.street}</span>
												<span>
													{shipmentDetail.deliveryAddress.zipCode}
													<span className="ml-2">
														{shipmentDetail.deliveryAddress.city}
													</span>
												</span>
											</dd>
										</>
									)}
									{shipmentDetail.senderAddress && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/dispatchedfrom')}
											</dt>
											<dd className="flex flex-col gap-2">
												<span>{shipmentDetail.senderAddress.name}</span>
												<span>{shipmentDetail.senderAddress.street}</span>
												<span>
													{shipmentDetail.senderAddress.zipCode}
													<span className="ml-2">
														{shipmentDetail.senderAddress.city}
													</span>
												</span>
											</dd>
										</>
									)}
									{shipmentDetail.goodsReceiver && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/goodsreceiver')}
											</dt>
											<dd>{shipmentDetail.goodsReceiver}</dd>
										</>
									)}
									{shipmentDetail.phoneNumber && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/phonenumber')}
											</dt>
											<dd>{shipmentDetail.phoneNumber}</dd>
										</>
									)}
									{shipmentDetail.projectName && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/projectname')}
											</dt>
											<dd>{shipmentDetail.projectName}</dd>
										</>
									)}
									{shipmentDetail.projectNumber && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/projectnumber')}
											</dt>
											<dd>{shipmentDetail.projectNumber}</dd>
										</>
									)}
									{shipmentDetail.requisitionNumber && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/requisitionnumber')}
											</dt>
											<dd>{shipmentDetail.requisitionNumber}</dd>
										</>
									)}
									{shipmentDetail.carrier && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/carrier')}
											</dt>
											<dd>{shipmentDetail.carrier}</dd>
										</>
									)}
									{shipmentDetail.resourcesVehicle && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/resource')}
											</dt>
											<dd>{shipmentDetail.resourcesVehicle}</dd>
										</>
									)}
									{shipmentDetail.carrierLink && (
										<>
											<dt className="text-gray-600">
												{t('shipmentpage/detail/carrierlink')}
											</dt>
											<dd>{shipmentDetail.carrierLink}</dd>
										</>
									)}
								</dl>
							</div>
						)}
					</div>
				)}
			</Layout>
		</main>
	);
};

export default ShipmentPage;
